import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ClinicStaffMember {
  id: string; // This is profiles.id for FK compatibility
  name: string;
  email: string;
  role: string;
  credentials?: string | null;
}

export function useClinicStaff() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['clinic-staff', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return [];

      // Fetch profiles (for FK-compatible IDs) and clinic_members (for roles)
      const [profilesResult, membersResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, email, credentials')
          .eq('clinic_id', profile.clinic_id)
          .order('full_name'),
        supabase
          .from('clinic_members')
          .select('email, role')
          .eq('clinic_id', profile.clinic_id),
      ]);

      if (profilesResult.error) throw profilesResult.error;

      // Create a map of email -> role from clinic_members
      const roleMap = new Map<string, string>();
      (membersResult.data ?? []).forEach((m) => {
        roleMap.set(m.email.toLowerCase(), m.role);
      });

      // Merge profiles with roles, using 'staff' as default
      return (profilesResult.data ?? []).map((p) => ({
        id: p.id,
        name: p.full_name,
        email: p.email,
        role: roleMap.get(p.email.toLowerCase()) ?? 'staff',
        credentials: p.credentials,
      })) as ClinicStaffMember[];
    },
    enabled: !!profile?.clinic_id,
  });
}
